<template>
  <div>
    <a
      class="v-list-item transition-colors duration-200"
      :class="{ 'v-list-item--disabled': mustAcceptTerms }"
      :disabled="mustAcceptTerms"
      @click="onSearch"
    >
      <div class="v-list-item__content">
        <div class="flex flex-column align-center gap-2 pa-2 rounded bg-gray-100 hover:bg-gray-200 transition-colors duration-200 inline-block">
          <v-icon>mdi-magnify</v-icon>
          <span class="text-black">Realizar reserva</span>
        </div>
      </div>
    </a>

    <menu-item
      :item="tab"
      :key="tab.id"
      v-for="tab in tabs"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import MenuItem from '@/components/menu/MenuItem';
import MenuItemMixin from '@/mixins/MenuItemMixin';

export default {
  name: 'DoctorMenu',

  mixins: [
    MenuItemMixin,
  ],

  components: {
    MenuItem,
  },

  computed: {
    ...mapGetters('user', {
      isCalendarEnabled: 'GET_CALENDAR_ENABLED',
      readOnly: 'GET_READONLY_INFO',
      userInfo: 'GET_USER_INFO',
      subscription: 'GET_SUBSCRIPTION',
    }),
    ...mapGetters('validateUser', [ 'GET_OWN_DOCTOR_VALIDATION' ]),
    ...mapGetters('team', {
      isTeamManager: 'GET_IS_TEAM_MANAGER',
      isTeamOwner: 'GET_IS_TEAM_OWNER',
      teamRole: 'GET_ROLE',
    }),
    ...mapGetters('termsOfUse', {
      termsAccepted: 'ACCEPTED'
    }),

    mustAcceptTerms() {
      return this.termsAccepted === false;
    },

    tabs() {
      const tabs = [];
      const allManagerAllowed = process.env.ALLOWED_MANAGERS === 'true';
      const showTeam = !this.isTeamUser && (
        allManagerAllowed || this.isTeamEnabled
      ) || this.isTeamManager;

      tabs.push(
        {
          active: this.isActive('doctor-dashboard'),
          color: 'blue',
          icon: 'mdi-speedometer',
          to: 'doctor-dashboard',
          title: this.teamRole === 'receptionist'
            ? this.$t('menu.receptionistDashboard')
            : this.$t('menu.doctorDashboard'),
        },
        this.isTeamUser
          ? null
          : {
            color: 'green',
            icon: 'mdi-cog-outline',
            title: this.$t('menu.myHabitat'),
            submenus: [
              {
                active: this.isActive('habitat'),
                color: 'green',
                title: this.$t('menu.businessIntelligenceCenter'),
                to: 'habitat',
              },
              showTeam ? {
                active: this.isActive('team-users'),
                color: 'green',
                title: this.$t('menu.team'),
                to: 'team-users',
              } : null,
            ]
          },
        this.isTeamMember
          ? null
          : !this.hasActiveSubscription
            ? this.habitatSubscriptionMenu
            : {
                ...this.habitatSubscriptionMenu,
                submenus: [
                  {
                    active: this.isActive('doctor-subscription'),
                    color: this.habitatSubscriptionMenu.color,
                    title: this.$t('menu.subscription.dashboard'),
                    to: 'doctor-subscription',
                  },
                  {
                    active: this.isActive('doctor-secretary'),
                    color: this.habitatSubscriptionMenu.color,
                    disabled: !this.isVirtualSecretaryEnabled,
                    title: this.$t('menu.virtualSecretary'),
                    to: 'doctor-secretary',
                  },
                  {
                    disabled: !this.isVirtualSecretaryEnabled,
                    color: this.habitatSubscriptionMenu.color,
                    submenus: [
                      {
                        active: this.isActive('doctor-habitat-performance-dashboard'),
                        color: this.habitatSubscriptionMenu.color,
                        title: this.$t('menu.habitatPerformanceDashboard'),
                        to: 'doctor-habitat-performance-dashboard',
                      },
                      {
                        active: this.isActive('doctor-habitat-performance-analysis'),
                        color: this.habitatSubscriptionMenu.color,
                        title: this.$t('menu.habitatPerformanceAnalysis'),
                        to: 'doctor-habitat-performance-analysis',
                      },
                      {
                        active: this.isActive('doctor-habitat-performance-insights'),
                        color: this.habitatSubscriptionMenu.color,
                        customBadge: {
                          color: 'blue',
                          label: 'Prime',
                          outline: true,
                        },
                        disabled: !this.hasPaidPlan,
                        title: this.$t('menu.habitatPerformanceInsights'),
                        to: 'doctor-habitat-performance-insights',
                      },
                      {
                        active: this.isActive('doctor-habitat-performance-content-production'),
                        color: this.habitatSubscriptionMenu.color,
                        customBadge: {
                          color: 'blue',
                          label: 'Prime',
                          outline: true,
                        },
                        disabled: !this.hasPaidPlan,
                        title: this.$t('menu.habitatPerformanceContentProduction'),
                        to: 'doctor-habitat-performance-content-production',
                      },
                      {
                        active: this.isActive('doctor-habitat-performance-library'),
                        color: this.habitatSubscriptionMenu.color,
                        customBadge: {
                          color: 'blue',
                          label: 'Prime',
                          outline: true,
                        },
                        disabled: !this.hasPaidPlan,
                        title: this.$t('menu.habitatPerformanceLibrary'),
                        to: 'doctor-habitat-performance-library',
                      },
                    ],
                    title: this.$t('menu.habitatPerformance'),
                  },
                ],
              },
        this.isCalendarEnabled ? this.calendarMenu : null,
        {
          color: 'blue',
          icon: 'mdi-domain',
          title: this.$t('menu.mySpaces'),
          submenus: [
            {
              active: this.isActive('search'),
              color: 'blue',
              title: this.$t('menu.makeSchedule'),
              to: 'search',
            },
            {
              active: this.isActive('doctor-calendar-events'),
              color: 'blue',
              title: this.$t('menu.calendar.events'),
              to: 'doctor-calendar-events',
            },
            {
              active: this.isActive('doctor-events'),
              color: 'blue',
              title: this.$t('menu.doctorEvents'),
              to: 'doctor-events',
            },
            this.isTeamManager && !this.isTeamOwner
              ? null
              : {
                active: this.isActive('doctor-products'),
                color: 'blue',
                title: this.$t('menu.myProducts'),
                to: 'doctor-products',
              },
          ],
        },
        this.isTeamManager
          ? null
          : {
            active: this.isActive('doctor-services'),
            color: 'blue',
            icon: 'mdi-room-service',
            title: this.$t('menu.services'),
            to: 'doctor-services',
          },
        {
          active: this.isActive('doctor/marketplace'),
          color: 'blue',
          customBadge: {
            color: 'blue',
            label: 'Novo',
            outline: true,
          },
          disabled: !this.isDoctorMarketplaceEnabled,
          title: this.$t('menu.marketplace'),
          icon: 'mdi-shopping',
          to: 'doctor/marketplace',
        },
      );

      return tabs
        .filter(Boolean)
        .filter((t) => !t.hidden);
    },

    calendarMenu() {
      const color = 'purple';

      return {
        color,
        icon: 'mdi-calendar-month',
        submenus: [
          {
            active: this.isActive('doctor-calendar-settings'),
            color,
            title: this.$t('menu.calendar.settings'),
            to: 'doctor-calendar-settings',
          },
          {
            active: this.isActive('doctor-schedules-slots'),
            color,
            title: this.$t('menu.calendar.schedule.slots'),
            to: 'doctor-schedules-slots',
          },
          {
            active: this.isActive('doctor-schedules-list'),
            color,
            title: this.$t('menu.calendar.schedule.list'),
            to: 'doctor-schedules-list',
          },
          {
            active: this.isActive('doctor-patients'),
            color,
            title: this.$t('menu.doctorPatients'),
            to: 'doctor-patients',
          },
          {
            active: this.isActive('doctor-transcriptions'),
            color,
            title: this.$t('menu.subscription.transcription.reports'),
            to: 'doctor-transcriptions',
          },
        ],
        title: this.$t('menu.calendar.doctor'),
      }
    },

    habitatSubscriptionMenu() {
      return {
        color: 'blue',
        disabled: false,
        icon: 'mdi-file-document-outline',
        title: this.$t('menu.subscriptions'),
        to: 'doctor-subscription',
      };
    },

    isTeamEnabled() {
      return this.readOnly?.teamEnabled;
    },

    isDoctorValidated() {
      return this.readOnly?.isDoctorValidated;
    },

    isDoctorMarketplaceEnabled() {
      return this.readOnly?.isDoctorMarketplaceEnabled || false;
    },

    isTeamUser() {
      return this.teamRole === 'user';
    },

    isTeamMember() {
      return this.teamRole && this.teamRole !== 'owner';
    },

    hasActiveSubscription() {
      return this.subscription?.status === 'active';
    },

    hasPaidPlan() {
      return this.subscription?.plan !== 'free';
    },

    isVirtualSecretaryEnabled() {
      return this.hasActiveSubscription && this.hasPaidPlan;
    },
  },

  methods: {
    onSearch() {
      this.$router.push({ name: 'search' });
    },
  }
}
</script>
